import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchCompanyInfo, 
  selectCompanyInfo, 
  selectCompanyLoading 
} from '../app/store/slices/companySlice';

/**
 * Custom hook to get store info with automatic fetching and caching
 * 
 * Usage:
 * const { storeInfo, loading } = useStoreInfo();
 * 
 * Features:
 * - Automatically fetches from store-info API if not in store
 * - Caches in Redux store for subsequent calls
 * - No duplicate API calls across components
 */
export const useStoreInfo = () => {
  const dispatch = useDispatch();
  const storeInfo = useSelector(selectCompanyInfo);
  const loading = useSelector(selectCompanyLoading);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once if not already loaded, not currently loading, and haven't tried fetching yet
    if (!storeInfo && !loading && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchCompanyInfo());
    }
  }, [dispatch]); // Remove storeInfo and loading from dependencies to prevent infinite loop

  return { storeInfo, loading };
};

// Keep old export for backward compatibility during transition
export const useCompanyInfo = () => {
  const { storeInfo, loading } = useStoreInfo();
  return { companyInfo: storeInfo, loading };
};

export default useStoreInfo;
